import type { AiCompanionAttendanceProposal, AiCompanionProposedAttendee } from "@simbelapp/order-sdk";
import { AttendanceNameMatchEnum, AttendanceSignatureStatusEnum } from "~/utils/enums/attendance-review.enums";
import type {
  IAttendanceMissingRow,
  IAttendanceSignatureRow,
  IAttendanceSignatureView,
} from "~/utils/interfaces/attendance-review-interfaces";

/** API constraint for every confirmed attendee entry. */
export const ATTENDANCE_MAX_HOURS = 24;

/**
 * How the backend tied this line to a collaborator. Mapped member by member rather than cast: the
 * SDK ships no declarations, so `nameMatch` reaches us untyped and an unknown value has to degrade
 * to `null` — which is the pre-`nameMatch` behaviour.
 */
export function nameMatchOf(attendee: AiCompanionProposedAttendee): AttendanceNameMatchEnum | null {
  switch (attendee.nameMatch) {
    case AttendanceNameMatchEnum.EXACT:
      return AttendanceNameMatchEnum.EXACT;
    case AttendanceNameMatchEnum.AI_SUGGESTED:
      return AttendanceNameMatchEnum.AI_SUGGESTED;
    default:
      return null;
  }
}

/**
 * Why a sheet line is flagged. Order is severity: an unrecognised name beats a doubtful identity,
 * which beats an unbooked collaborator — an AI pairing has to be checked whether or not the person
 * it points at is on the session.
 */
export function signatureStatusOf(
  row: IAttendanceSignatureRow,
  enrolledUserIds: Set<string>,
): AttendanceSignatureStatusEnum {
  if (!row.userId) return AttendanceSignatureStatusEnum.UNRECOGNIZED;
  if (row.nameMatch === AttendanceNameMatchEnum.AI_SUGGESTED) return AttendanceSignatureStatusEnum.AI_SUGGESTED;
  if (!enrolledUserIds.has(row.userId)) return AttendanceSignatureStatusEnum.NOT_ENROLLED;

  return AttendanceSignatureStatusEnum.MATCHED;
}

/** Confirming a line that points at somebody unbooked adds them to the session — always said. */
const ENROLMENT_NOTE = "Non inscrit à la session — sera ajouté en validant";

/** The sentence under the picker. */
export function signatureMessageOf(status: AttendanceSignatureStatusEnum): string | null {
  switch (status) {
    case AttendanceSignatureStatusEnum.UNRECOGNIZED:
      return "Nom lu non reconnu";
    case AttendanceSignatureStatusEnum.AI_SUGGESTED:
      return "Associé par l'IA — orthographe proche";
    case AttendanceSignatureStatusEnum.NOT_ENROLLED:
      return ENROLMENT_NOTE;
    default:
      return null;
  }
}

/** Green when the pairing is settled, amber when the identity is in doubt, blue when it is news. */
export const ATTENDANCE_STATUS_ICON: Record<AttendanceSignatureStatusEnum, string> = {
  [AttendanceSignatureStatusEnum.MATCHED]: "validate_green",
  [AttendanceSignatureStatusEnum.NOT_ENROLLED]: "info",
  [AttendanceSignatureStatusEnum.AI_SUGGESTED]: "warning",
  [AttendanceSignatureStatusEnum.UNRECOGNIZED]: "warning",
};

export const ATTENDANCE_STATUS_COLOR: Record<AttendanceSignatureStatusEnum, string> = {
  [AttendanceSignatureStatusEnum.MATCHED]: "",
  [AttendanceSignatureStatusEnum.NOT_ENROLLED]: "text-primary-blue-color",
  [AttendanceSignatureStatusEnum.AI_SUGGESTED]: "text-primary-orange-color",
  [AttendanceSignatureStatusEnum.UNRECOGNIZED]: "text-primary-orange-color",
};

/** Only an identity the admin still has to rule on gets the amber control. */
export const ATTENDANCE_WARNED_STATUSES: AttendanceSignatureStatusEnum[] = [
  AttendanceSignatureStatusEnum.AI_SUGGESTED,
  AttendanceSignatureStatusEnum.UNRECOGNIZED,
];

/**
 * Everything the table paints for one line. Derived rather than stored: picking another
 * collaborator has to move the icon, the message and the enrolment note together.
 */
export function signatureViewOf(row: IAttendanceSignatureRow, enrolledUserIds: Set<string>): IAttendanceSignatureView {
  const status = signatureStatusOf(row, enrolledUserIds);
  /*
   * An AI pairing spends the row's one message on the identity doubt, which would otherwise hide
   * that the collaborator is not booked either. Both matter, so the second one gets its own line.
   */
  const isUnbooked = status === AttendanceSignatureStatusEnum.AI_SUGGESTED && !enrolledUserIds.has(row.userId ?? "");

  return {
    status,
    message: signatureMessageOf(status),
    enrolmentNote: isUnbooked ? ENROLMENT_NOTE : null,
    missingHours: Math.max(row.totalHours - row.attendedHours, 0),
  };
}

function toSignatureRow(
  attendee: AiCompanionProposedAttendee,
  index: number,
  isMatched: boolean,
): IAttendanceSignatureRow {
  return {
    key: `${attendee.nameAttendee ?? attendee.displayValue}-${index}`,
    nameAttendee: attendee.nameAttendee ?? attendee.displayValue,
    userId: attendee.userId ?? null,
    displayValue: attendee.userId ? attendee.displayValue : null,
    /*
     * A line the backend could not place in the session carries no hours of its own: the signature
     * was read, so it counts for the whole session until the admin says otherwise.
     */
    attendedHours: isMatched ? attendee.attendedHours : attendee.attendedHours || attendee.totalHours,
    totalHours: attendee.totalHours,
    nameMatch: nameMatchOf(attendee),
  };
}

function toMissingRow(attendee: AiCompanionProposedAttendee): IAttendanceMissingRow {
  return {
    userId: attendee.userId ?? "",
    displayValue: attendee.displayValue,
    // "laissez à 0 pour les marquer absents" — absent is the default for a missing signature.
    attendedHours: 0,
    totalHours: attendee.totalHours,
  };
}

/**
 * One row per booked collaborator, which `unsignedRows` narrows to those no sheet line declares.
 *
 * Seeded from the whole roster rather than from `notFoundInFile` alone: a collaborator the analysis
 * *did* place is only in the confirm payload for as long as a line points at them, and unpairing
 * that line drops them from it — which the backend records as absent. Filed here, they come back
 * into the modal the moment they stop being paired, and the admin rules on them like any no-show.
 */
function toBookedRows(proposal: AiCompanionAttendanceProposal): IAttendanceMissingRow[] {
  // A booked collaborator always has an id; without one there is nothing the modal could declare.
  const unsigned = (proposal.notFoundInFile ?? []).filter((attendee) => !!attendee.userId).map(toMissingRow);
  const known = new Set(unsigned.map((row) => row.userId));

  return [
    ...unsigned,
    // The rest of the roster: `matched` carries them too, but with the sheet's hours rather than
    // the zero the modal opens at, so they are read off `sessionUsers` instead.
    ...(proposal.sessionUsers ?? [])
      .filter((user) => !known.has(user.userId))
      .map((user) => ({
        userId: user.userId,
        displayValue: user.fullname,
        attendedHours: 0,
        totalHours: proposal.totalHours,
      })),
  ];
}

/**
 * The rows the confirm modal actually asks about: booked collaborators no sheet line points at.
 * Read off the pairings rather than off the analysis, in both directions — pairing a line to
 * somebody the backend filed as absent from the sheet takes them out, and clearing a line the
 * backend had placed puts that collaborator back in.
 */
export function unsignedRowsOf(
  bookedRows: IAttendanceMissingRow[],
  signatures: IAttendanceSignatureRow[],
): IAttendanceMissingRow[] {
  const paired = new Set(signatures.map((row) => row.userId));

  return bookedRows.filter((row) => !paired.has(row.userId));
}

/**
 * Splits the proposal along the axis the screen uses, which is not the one the API uses: the table
 * is one row per signature read (`matched` + `notFoundInSession`, whether or not the backend could
 * place it), and the roster is ruled on separately, in the modal that precedes the write.
 */
export function buildReviewRows(proposal: AiCompanionAttendanceProposal): {
  signatures: IAttendanceSignatureRow[];
  bookedRows: IAttendanceMissingRow[];
} {
  const matched = proposal.matched ?? [];
  // `matched` first keeps the sheet's own order, with the lines it could not place at the end.
  const lines = [...matched, ...(proposal.notFoundInSession ?? [])];

  return {
    signatures: lines.map((attendee, index) => toSignatureRow(attendee, index, index < matched.length)),
    bookedRows: toBookedRows(proposal),
  };
}
